// import './overview.css'

import clsx from 'clsx';
import DateTimePicker from 'react-datetime-picker';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { DashboardOutlined, Info, Clear } from '@mui/icons-material';
import { Container, Paper, Grid, Button, InputLabel, InputAdornment, IconButton, OutlinedInput, FormControl, Tooltip, Select, Menu, MenuItem, Box, TextField } from '@mui/material';

import * as FileSaver from 'file-saver';
import XLSX from 'sheetjs-style';

import logo from '../images/logo.svg';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useDeferredValue } from 'react';
import axios from '../../helpers/request';
import moment from 'moment';

import styled from '@emotion/styled'
import { css } from '@emotion/react'

const MainContainer = styled.main(({ theme }) => ({
  flexGrow: 1,
  minHeight: '100vh',
  overflow: 'auto',
  backgroundColor: '#f8fafc',
  padding: 0
}))

// New header bar
const HeaderBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #e2e8f0',
});

const HeaderLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const LogoImage = styled.img({
  height: "28px",
});

const HeaderRight = styled.div({
  fontSize: '13px',
  fontWeight: 500,
  color: '#10416D',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

// Filter bar styles
const FilterBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px 24px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #e2e8f0',
  flexWrap: 'wrap',
  '& .react-datetime-picker__wrapper': {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
  },
  '& .react-datetime-picker__button': {
    padding: '0px 4px',
    opacity: '0.5',
  },
  '& .react-datetime-picker__inputGroup__input': {
    color: '#334155',
    fontSize: '14px',
  },
  '& .react-datetime-picker__inputGroup__input:invalid': {
    background: 'transparent',
  },
  '& .react-calendar': {
    position: 'fixed',
    zIndex: 1000,
  },
});

const FilterGroup = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const FilterLabel = styled.label({
  fontSize: '11px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

const SearchInput = styled.input({
  padding: '11px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#334155',
  backgroundColor: '#fff',
  outline: 'none',
  minWidth: '200px',
  '&:focus': {
    borderColor: '#10416D',
  },
  '&::placeholder': {
    color: '#94a3b8',
  },
});

const FilterSelect = styled.select({
  padding: '11px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#334155',
  backgroundColor: '#fff',
  outline: 'none',
  minWidth: '100px',
  cursor: 'pointer',
  '&:focus': {
    borderColor: '#10416D',
  },
});

const ExportButton = styled(Button)({
  backgroundColor: '#10416D',
  color: '#fff',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  padding: '8px 20px',
  marginTop: '20px',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: '#10416D',
  },
});

const ClearButton = styled(Button)({
  backgroundColor: '#fff',
  color: '#334155',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  padding: '8px 20px',
  borderRadius: '6px',
  marginTop: '20px',
  border: '1px solid #e2e8f0',
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
});

// Table container styles
const TableContainer = styled.div({
  padding: '24px 24px',
});

const TableCard = styled(Paper)({
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  '& .MuiDataGrid-root': {
    border: 'none',
    borderRadius: '0px !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '11px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .MuiDataGrid-cell': {
    fontSize: '14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .meeting-archive--true': {
    backgroundColor: '#FFEAE9',
  },
});

const ActionButton = styled(Button)(({ theme }) => (theme.actionButton))

const mavenStatusValues = [
  { title: 'Active', value: 'Active' },
  { title: 'Finished', value: 'Finished' },
  { title: 'Cancelled', value: 'Cancelled' },
  { title: 'Deleted', value: 'Deleted' },
]

export default function ZoomMeetings() {
  const listSortModel = {
    sortModel: [],
  }

  const [data, setData] = useState({
    data: [],
    total: 0
  });

  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [formDate, setFormDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [maven_status, setMavenStatus] = useState('all');
  const [queryOptions, setQueryOptions] = useState(listSortModel);
  const deferredSearch = useDeferredValue(search)

  const getLogs = () => {
    setLoading(true);
    setData({
      data: [],
      total: 0
    })
    let searchParams = {
      page_number: pageNumber,
      page_size: pageSize,
      search: deferredSearch,
      maven_status: maven_status === "all" ? "" : maven_status
    };

    if (queryOptions.sortModel[0] && queryOptions.sortModel[0].sort != 'null') {
      searchParams.order_by = queryOptions.sortModel[0].field + " " + queryOptions.sortModel[0].sort
    }

    if (formDate && toDate) {
      searchParams.from_date = moment(formDate).format('YYYY-MM-DD')
      searchParams.to_date = moment(toDate).format('YYYY-MM-DD')
    }

    axios.get('/api/zoom/meeting', { params: searchParams }).then(async (response) => {
      setLoading(false);
      if (response && response.status) {
        setData(response.data)
      } else {
        throw new Error(response?.error);
      }
    });
  }

  useEffect(() => {
    getLogs()
  }, [pageSize, pageNumber, deferredSearch, formDate, toDate, maven_status, queryOptions])

  const onChangePageSize = (pageSize) => {
    setPageSize(pageSize);
  }

  const onPageChange = (pageNumber) => {
    setPageNumber(pageNumber + 1);
  }

  const onChangeSearch = (searchValue) => {
    setSearch(searchValue.trim());
    setPageNumber(1);
  }

  const onChangeStatusFilter = (event) => {
    const value = event.target.value;
    setMavenStatus(value);
    setPageNumber(1);
  }

  const handleFromDateChange = (value) => {
    setFormDate(value)
  }
  const handleToDateChange = (value) => {
    setToDate(value)
  }

  const handleSortModelChange = useCallback((sortModel) => {
    console.log("handleSortModelChange", sortModel)
    setQueryOptions({ sortModel: [...sortModel] });
  }, []);

  const columns = [
    { field: 'topic', headerName: 'Topic', width: 200, sortable: false, filterable: false },
    { field: 'maven_id', headerName: 'Maven ID', width: 200, sortable: false, filterable: false },
    { field: 'source', headerName: 'Maven Source', width: 200, sortable: false, filterable: false },
    {
      field: 'meeting_id', headerName: 'Zoom Meeting ID', width: 300, sortable: false, filterable: false,
      renderCell: (params) => {
        return (
          <Link to={'/zoom-interaction/' + params.row.meeting_id}>
            {params.row.meeting_id}
          </Link>
        )
      }
    },
    { field: 'maven_status', headerName: 'Status', width: 110, sortable: false },
    {
      field: 'started_at', headerName: 'Start Time', width: 150, sortable: false, filterable: false,
      valueGetter: (params) => params ? moment(params).format('HH:mm') : 'NA'
    },
    {
      field: 'ended_at', headerName: 'End Time', width: 150, sortable: false, filterable: false,
      renderCell: (params) => params.row.ended_at ? params.row.meeting_ended_at ? moment(params.row.meeting_ended_at).format('HH:mm') : moment(params.row.ended_at).format('HH:mm') : 'NA',
    },
    {
      field: 'duration', headerName: 'Duration (Minutes)', width: 110, sortable: false, filterable: false,
      renderCell: (params) => {
        return (
          <span>
            {(params.row.started_at && params.row.ended_at) ? params.row.meeting_ended_at ? Math.ceil(moment(params.row.meeting_ended_at).diff(moment(params.row.started_at), 'minutes', true)) : Math.ceil(moment(params.row.ended_at).diff(moment(params.row.started_at), 'minutes', true)) : 'NA'}
          </span>
        )
      }
    },
    {
      field: 'recording', headerName: 'Recording', width: 110, sortable: false, filterable: false,
      renderCell: (params) => {
        return (
          <>
            {params.row.recording ? <b>Yes</b> : 'No'}
          </>
        )
      }
    },
    {
      field: 'anonymous', headerName: 'Anonymous', width: 110, sortable: false, filterable: false,
      renderCell: (params) => {
        return (
          <>
            {params.row.anonymous ? <b>Yes</b> : 'No'}
          </>
        )
      }
    },
    {
      field: 'disclaimer', headerName: 'Disclaimer', width: 110, sortable: false, filterable: false,
      renderCell: (params) => {
        return (
          <>
            {params.row.disclaimer ? <b>Yes</b> : 'No'}
          </>
        )
      }
    },
    { field: 'participant_count', headerName: 'Participants', width: 100, sortable: false, filterable: false },
    {
      field: 'link', headerName: 'Meeting Link', width: 310, sortable: false, filterable: false,
      renderCell: (params) => {
        return (
          <>
            {
              (params.row.started_at && !params.row.ended_at)
                ?
                <>
                  <ActionButton href={params.row.join_link} target="_blank" >
                    Join Meeting
                  </ActionButton>
                  <ActionButton href={params.row.chaperoning_link} target="_blank" >
                    Join As Chaperone Mode
                  </ActionButton>
                </>
                :
                ""
            }
          </>
        )
      }
    }
  ];

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAllClear = () => {
    setMavenStatus('all');
    setSearch('');
    setFormDate('');
    setToDate('');
    setPageNumber(1);
  }

  const meetingExcelHeaderRow = [['Topic', 'Maven ID', 'Zoom Meeting ID', 'Status', 'Interaction Date', 'Start Time (UTC)', 'End Time (UTC)', 'Duration', 'Recording', 'Anonymous Interaction', 'Disclaimer', 'Participants']];

  const getMeetingExcelData = (meetingData) => {
    const dataRows = meetingData.map(({ topic, maven_id, meeting_id, maven_status, started_at, ended_at, recording, anonymous, disclaimer, participant_count }) => (
      [topic, maven_id, meeting_id, maven_status, started_at ? moment(started_at).format('DD/MM/YYYY') : 'NA', started_at ? moment(started_at).format('HH:mm') : 'NA', ended_at ? moment(ended_at).format('HH:mm') : 'NA', (started_at && ended_at) ? Math.ceil(moment(ended_at).diff(moment(started_at), 'minutes', true)) : 'NA', recording ? 'Yes' : 'No', anonymous ? 'Yes' : 'No', disclaimer ? 'Yes' : 'No', participant_count]
    ))

    return [...meetingExcelHeaderRow, ...dataRows];
  }

  const exportMeetingInteraction = (meetingData) => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = ".xlsx";
    const headerCellBackgroundColor = "4A86E8";
    const headerCellFontColor = "FFFFFF";
    const maxColCounts = meetingExcelHeaderRow[0].length + 65;

    const ws = XLSX.utils.aoa_to_sheet(getMeetingExcelData(meetingData));

    for (let i = 65; i < maxColCounts; i++) {
      ws[`${String.fromCharCode(i) + 1}`].s = { fill: { patternType: "solid", fgColor: { rgb: headerCellBackgroundColor }, bgColor: { rgb: headerCellBackgroundColor } }, font: { color: { rgb: headerCellFontColor } } };
    }

    const wb = { Sheets: { 'meetings': ws }, SheetNames: ['meetings'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, `zoom_meetings_${new Date().getTime()}${fileExtension}`);

    setAnchorEl(null);
  };

  const participantExcelHeaderRow = [['Topic', 'Maven ID', 'Zoom Meeting ID', 'Participant Display Name', 'Participant Email', 'Join Time (UTC)', 'Leave Time (UTC)']];

  const getParticipantExcelData = (participantData) => {
    const dataRows = participantData.map(({ topic, maven_id, meeting_id, identity, join_time, leave_time, email }) => (
      [topic, maven_id, meeting_id, identity, email, join_time ? moment(join_time).format('HH:mm') : 'NA', leave_time ? moment(leave_time).format('HH:mm') : 'NA']
    ))

    return [...participantExcelHeaderRow, ...dataRows];
  }

  const exportParticipantInteraction = async (meetingData) => {
    const meetingIds = meetingData.map(({ meeting_id }) => (meeting_id))

    try {
      const response = await axios.post('/api/zoom/meeting-participants', { meeting_ids: meetingIds });

      if (response.status === 200) {
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = ".xlsx";
        const headerCellBackgroundColor = "4A86E8";
        const headerCellFontColor = "FFFFFF";
        const maxColCounts = participantExcelHeaderRow[0].length + 65;

        const ws = XLSX.utils.aoa_to_sheet(getParticipantExcelData(response.data));
        for (let i = 65; i < maxColCounts; i++) {
          ws[`${String.fromCharCode(i) + 1}`].s = { fill: { patternType: "solid", fgColor: { rgb: headerCellBackgroundColor }, bgColor: { rgb: headerCellBackgroundColor } }, font: { color: { rgb: headerCellFontColor } } };
        }
        const wb = { Sheets: { 'participants': ws }, SheetNames: ['participants'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, `zoom_participants_${new Date().getTime()}${fileExtension}`);

      }
      setAnchorEl(null);
    } catch (error) {
      alert("Unable to import data");
      setAnchorEl(null);
    }
  };

  return (
    <MainContainer >

      {/* Header Bar */}
      <HeaderBar>
        <HeaderLeft>
          <LogoImage src={logo} alt="Dialectica" />
        </HeaderLeft>
        <HeaderRight>ZOOM MEETINGS</HeaderRight>
      </HeaderBar>

      {/* Filter Bar */}
      <FilterBar>
        <FilterGroup>
          <FilterLabel>FROM DATE</FilterLabel>
          <DateTimePicker
            name='fromDate'
            value={formDate}
            onChange={handleFromDateChange}
            format='dd/MM/y'
            className='datepicker'
            disableClock
            required
            monthPlaceholder="MM"
            dayPlaceholder="DD"
            yearPlaceholder="YYYY"
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>TO DATE</FilterLabel>
          <DateTimePicker
            name='toDate'
            value={toDate}
            onChange={handleToDateChange}
            format='dd/MM/y'
            className='datepicker'
            disableClock
            required
            monthPlaceholder="MM"
            dayPlaceholder="DD"
            yearPlaceholder="YYYY"
          />
        </FilterGroup>

        <FilterGroup style={{ flex: 1, minWidth: '200px' }}>
          <FilterLabel>Topic/MeetingID/MavenID</FilterLabel>
          <SearchInput
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(event) => { onChangeSearch(event.target.value) }}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect
            value={maven_status}
            onChange={onChangeStatusFilter}
          >
            <option value="all">All</option>
            {mavenStatusValues.map(option =>
              <option key={option.value} value={option.value}>{option.title}</option>
            )}
          </FilterSelect>
        </FilterGroup>

        <ExportButton
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={handleClick}
        >
          Export
        </ExportButton>

        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={() => exportMeetingInteraction(data.data)}>Interaction Report</MenuItem>
          <MenuItem onClick={() => exportParticipantInteraction(data.data)}>Participant Report</MenuItem>
        </Menu>

        <ClearButton onClick={handleAllClear}>
          Clear All
        </ClearButton>
      </FilterBar>

      {/* Table Container */}
      <TableContainer>
        <TableCard elevation={0}>
          <DataGridPro
            getRowId={(row) => row.meeting_id}
            columns={columns}
            rows={data.data}
            rowCount={data.total}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            onPageSizeChange={(newPageSize) => onChangePageSize(newPageSize)}
            onPageChange={(newPage) => onPageChange(newPage)}
            autoHeight={true}
            pagination
            paginationMode="server"
            loading={loading}
            getRowClassName={(params) =>
              `meeting-archive--${params.row.is_deleted}`
            }
            initialState={{
              sorting: listSortModel
            }}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
          />
        </TableCard>
      </TableContainer>

    </MainContainer>
  );
}
